import { Loader } from '@googlemaps/js-api-loader';
import { CDN_URL, GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_ID } from './config';
import { Producer } from '../models/producer';
import { getMarkerIcons } from './getMarkerIcons';
import { Renderer } from '@googlemaps/markerclusterer';

export const MAX_ZOOM = 14;
export const MIN_ZOOM = 3;

export async function initMap(mapElement: HTMLElement) {
  const loader = new Loader({
    apiKey: GOOGLE_MAPS_API_KEY,
    version: 'weekly',
  });

  const importMaps = loader.importLibrary('maps');
  const importMarker = loader.importLibrary('marker');

  const [{ Map }, { AdvancedMarkerElement }] = await Promise.all([importMaps, importMarker]);

  let map: google.maps.Map | null = null;

  if (typeof mapElement !== 'undefined' || mapElement !== null) {
    map = new Map(mapElement, {
      center: { lat: 33.9058855, lng: 65.0644345 },
      zoom: 4,
      mapId: GOOGLE_MAPS_ID,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      clickableIcons: false,
      // TODO: only on mobile
      //gestureHandling: 'cooperative',
      maxZoom: MAX_ZOOM,
      minZoom: MIN_ZOOM,
    });
  }

  function createMarker({ position, name, category }: Producer): typeof AdvancedMarkerElement {
    const { DEFAULT_ICON_URL, SCALED_SIZE } = getMarkerIcons(category);
    const img = document.createElement('img');
    img.src = DEFAULT_ICON_URL;
    img.height = SCALED_SIZE.height;
    img.width = SCALED_SIZE.width;

    return new AdvancedMarkerElement({
      map,
      position,
      title: name,
      content: img,
      gmpClickable: true,
    });
  }

  function createCustomCluster(map: google.maps.Map<Element> | null | undefined, defaultIconUrl: string): Renderer {
    return {
      render: ({ count, position, markers }) => {
        const label = document.createElement('span');
        label.insertAdjacentText('beforeend', count.toString());
        label.style.color = 'black';
        label.style.fontSize = '16px';
        label.style.fontFamily = 'Helvetica Neue, sans-serif';
        label.style.fontWeight = 'bold';

        const labelContainer = document.createElement('div');
        labelContainer.style.position = 'absolute';
        labelContainer.style.display = 'flex';
        labelContainer.style.justifyContent = 'center';
        labelContainer.style.alignItems = 'center';
        labelContainer.style.width = '100%';
        labelContainer.style.height = '100%';
        labelContainer.appendChild(label);

        const img = document.createElement('img');
        img.src = defaultIconUrl;
        img.height = 40;
        img.width = 40;

        const markerContainer = document.createElement('div');
        markerContainer.style.position = 'relative';
        markerContainer.style.display = 'flex';
        markerContainer.appendChild(img);
        markerContainer.appendChild(labelContainer);

        return new AdvancedMarkerElement({
          map: map,
          position,
          content: markerContainer,
          title: getClusterTitle(markers),
          gmpClickable: true,
          zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
        });
      },
    };
  }

  const getClusterTitle = (markers) => {
    const producerNames: string[] = markers.map(({ title }: { title: string }) => title);
    const nameShortList = (producerNames.length > 2 ? producerNames.slice(0, 2) : producerNames).join(', ');

    return `${producerNames.length} producers: ${nameShortList}${producerNames.length > 2 ? '...' : ''}`;
  };

  const householdCluster = (map: google.maps.Map<Element> | null | undefined) =>
    createCustomCluster(map, `${CDN_URL}/static/1.0.0/household-circle-default.png`);
  const clothingCluster = (map: google.maps.Map<Element> | null | undefined) =>
    createCustomCluster(map, `${CDN_URL}/static/1.0.0/clothing-circle-default.png`);
  const stationeryCluster = (map: google.maps.Map<Element> | null | undefined) =>
    createCustomCluster(map, `${CDN_URL}/static/1.0.0/stationery-circle-default.png`);

  return { map, createMarker, householdCluster, clothingCluster, stationeryCluster };
}
